import React, { useCallback, useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatDate, formatDateTime } from "@/lib/api";
import { AttHolidays } from "@/components/AttHolidays";

const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ST = {
  missing: ["Not taken", "bg-red-50 text-red-700 ring-red-200"],
  taken: ["Taken", "bg-green-50 text-green-700 ring-green-200"],
  holiday: ["Holiday", "bg-sky-50 text-sky-700 ring-sky-200"],
  weekly_off: ["Weekly off", "bg-slate-100 text-slate-600 ring-slate-200"],
};
const ORDER = { missing: 0, holiday: 1, weekly_off: 2, taken: 3 };

function Row({ i }) {
  const [label, cls] = ST[i.status];
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`notaken-row-${i.date || "d"}-${i.teacher_id}-${i.klass}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900">{i.date && <span className="text-slate-500 font-normal">{formatDate(i.date)} · </span>}{i.teacher_name} · {i.klass}</p>
        <p className="text-xs text-slate-500">
          {i.status === "taken" ? `Saved ${formatDateTime(i.saved_at)}` : i.holiday_reason ? `${i.holiday_reason}${i.holiday_by ? ` · by ${i.holiday_by}` : ""}` : "No attendance saved for this class"}
        </p>
      </div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${cls}`} data-testid={`notaken-status-${i.teacher_id}-${i.klass}`}>{label}</span>
    </div>
  );
}

function WeeklyOff() {
  const [off, setOff] = useState(null);
  useEffect(() => { api.get("/admin/attendance-weekly-off").then(({ data }) => setOff(data.weekly_off)).catch(() => {}); }, []);
  const toggle = async (d) => {
    const next = off.includes(d) ? off.filter((x) => x !== d) : [...off, d];
    try { const { data } = await api.put("/admin/attendance-weekly-off", { weekly_off: next }); setOff(data.weekly_off); toast.success("Weekly off updated"); }
    catch { toast.error("Could not update"); }
  };
  if (!off) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="weekly-off">
      <span className="text-xs text-slate-500 mr-1">Weekly off:</span>
      {DAYS.map((n, d) => (
        <button key={n} type="button" onClick={() => toggle(d)} data-testid={`weekly-off-${n}`}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${off.includes(d) ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n}</button>
      ))}
    </div>
  );
}

export function NotTaken() {
  const [date, setDate] = useState(today());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    setData(null);
    try { const res = await api.get("/admin/attendance-missing", { params: { date, to } }); setData(res.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); setData({ items: [], counts: { missing: 0, taken: 0, holiday: 0, weekly_off: 0 }, day: "" }); }
  }, [date, to]);
  useEffect(() => { load(); }, [load]);
  const items = data ? [...data.items].sort((a, b) => ORDER[a.status] - ORDER[b.status] || (a.date || "").localeCompare(b.date || "")) : [];
  return (
    <div className="space-y-4" data-testid="admin-att-notaken">
      <div className="flex flex-wrap gap-4 items-end">
        <div><Label className="text-xs">From</Label><Input type="date" value={date} onChange={(e) => { setDate(e.target.value); if (e.target.value > to) setTo(e.target.value); }} className="w-40" data-testid="notaken-date" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} min={date} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="notaken-date-to" /></div>
        <WeeklyOff />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-blue-900" /> : (
        <>
          <div className={`rounded-xl p-3 flex items-center gap-2 text-sm font-semibold ${data.counts.missing ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`} data-testid="notaken-summary">
            <AlertTriangle className="h-4 w-4" />
            {data.day}: {data.counts.missing} not taken · {data.counts.taken} taken · {data.counts.holiday + data.counts.weekly_off} holiday/off
          </div>
          {items.length === 0 ? <p className="text-sm text-slate-500">No teachers with assigned classes.</p>
            : <div className="space-y-2">{items.map((i) => <Row key={`${i.date || ""}-${i.teacher_id}-${i.klass}`} i={i} />)}</div>}
        </>
      )}
    </div>
  );
}

export function AdminHolidays() {
  const [classes, setClasses] = useState([]);
  useEffect(() => {
    api.get("/admin/attendance-missing", { params: { date: today() } })
      .then(({ data }) => setClasses([...new Set(data.items.map((i) => i.klass))].sort())).catch(() => {});
  }, []);
  return <AttHolidays isAdmin classes={classes} />;
}
